import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../library/layout-geometry/coordinates';
import {
  getSnapVisualTypeForSnappedMousePosition,
  usePointPickingPointSnapper,
} from '../../library/layout-snapping/point-snapping/snapMousePosition';
import { setSelectedSnap } from '../../store/actions/selectActions';
import { getAllLayoutUserDistanceDimensions } from '../../store/selectors/layoutDataSelectors';
import { isLayoutViewActive } from '../../store/selectors/layoutModeSelectors';
import { ManualDistanceDimension } from '../dimension/ManualDistanceDimension';
import { SnapVisual } from '../dimension/SnapVisual';
import { useMousePositionInScene } from '../hooks/useMousePositionInScene';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';

export function LayoutDimensionModeController() {
  const layoutUserDistanceDimensions = useAppSelector(getAllLayoutUserDistanceDimensions);
  const inDimensionMode = useAppSelector((state) => state.application.inDimensionMode);
  const layout = useAppSelector(isLayoutViewActive);

  if (!layout) return null;

  return (
    <>
      {layoutUserDistanceDimensions.map((dimension) => (
        <ManualDistanceDimension
          key={`${dimension.start.x},${dimension.start.y}-${dimension.end.x},${dimension.end.y}`}
          segment={{ start: dimension.start, end: dimension.end }}
        />
      ))}

      {inDimensionMode && <DimensionModePointPickingVisualsAndEventListeners />}
    </>
  );
}

/**
 * For picking dimensioning points we need to use different settings than `PointPickingVisualsAndEventListeners`
 */
function DimensionModePointPickingVisualsAndEventListeners() {
  const dispatch = useAppDispatch();
  const snapMousePosition = usePointPickingPointSnapper({ entitySnappingOnly: true });
  const mousePosition = useMousePositionInScene();

  useEffect(() => {
    function onSceneClick(event: GlobalThreeJSDomObjectEvent) {
      const clickPosition2D = convertFromDesignToLayoutCoordinates(event.eventPosition.worldPosition).projectTo2D();
      const snapResult = snapMousePosition(clickPosition2D);
      dispatch(setSelectedSnap(snapResult.point.toObject()));
    }

    threeJsDom.addGlobalListener('click', onSceneClick);
    return () => void threeJsDom.removeGlobalListener('click', onSceneClick);
  });

  if (mousePosition == null) return null;

  const originalMousePosition2D = convertFromDesignToLayoutCoordinates(mousePosition).projectTo2D();
  const snapResult = snapMousePosition(originalMousePosition2D);
  const snapVisualType = getSnapVisualTypeForSnappedMousePosition(snapResult);

  if (snapVisualType == null) return null;
  return <SnapVisual snapType={snapVisualType} position={snapResult.point.to3D().toArray()} />;
}
